import React, { useEffect } from 'react';
import { Modal } from '../';
import { Form, Alert, Spin, Typography } from 'antd';
import { CredentialSetSelector } from './CredentialSetSelector';
import { TargetCloudSelector, MigrationTypeSelector, MigrationOptionsCheckboxes } from './components';
import { useMigrationReadinessForm, useCloudAccountProcessing } from './hooks';
import './MigrationReadinessDialog.scss';

const { Text } = Typography;

/**
 * Refactored Migration Readiness Check Dialog component using modular architecture
 *
 * This dialog allows users to select a target cloud and configure migration settings
 * before running preflight checks. Based on the Backbone TargetSelectorView component.
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onClose - Handler for dialog close
 * @param {Function} onConfirm - Handler for dialog confirmation with migration settings
 * @param {Array} selectedSources - Array of selected source objects
 * @param {string} title - Dialog title
 * @param {boolean} inspectionMode - Whether this is source inspection mode (shows only target cloud and stored info)
 * @param {string} okTextOverride - Optional override text for the OK button
 * @param {boolean} showIgnoreValidationErrors - Force show Ignore Validation Errors even in inspectionMode
 */
export const MigrationReadinessDialog = ({
    open,
    onClose,
    onConfirm,
    selectedSources = [],
    title = 'Migration Readiness Check',
    inspectionMode = false,
    okTextOverride,
    showIgnoreValidationErrors = false,
}) => {
    // Use custom hooks for state management
    const {
        // Form state
        form,
        selectedAccountId,
        selectedAccount,
        migrationType,
        credentialSets,
        showCredentialSelector,
        loading,

        // Derived state
        selectedSourcesWithCACreds,
        migrationTypeFlags,
        showUseStoredInfoCheckbox,

        // Handlers
        handleTargetAccountChange,
        handleMigrationTypeChange,
        handleCredentialSetChange,
        handleConfirm,
    } = useMigrationReadinessForm({ open, selectedSources, onConfirm });

    // Cloud account processing and auto-selection
    const { isLoadingAccounts, accountsError, processedCloudAccounts, shouldPreselect } = useCloudAccountProcessing({
        selectedSources,
        migrationTypeFlags,
        selectedAccountId,
        handleTargetAccountChange,
        form,
        title,
    });

    const okText = okTextOverride || (inspectionMode ? 'Inspect Sources' : 'Check Migration Readiness');

    useEffect(
        () => {
            if (migrationType === 'block_mobility' && !inspectionMode) {
                form.setFieldValue('blockMobilityType', 'existing_disk_on_host');
            }
        },
        [selectedAccount?.cloud_type],
        migrationType,
        inspectionMode,
    );

    return (
        <Modal
            title={title}
            open={open}
            onCancel={onClose}
            onOk={handleConfirm}
            okText={okText}
            cancelText="Close"
            width={600}
            confirmLoading={loading}
            className="migration-readiness-dialog"
        >
            <div className="migration-readiness-content">
                {isLoadingAccounts ? (
                    <div className="loading-container">
                        <Spin size="large" />
                        <Text>Loading available Target Clouds...</Text>
                    </div>
                ) : accountsError ? (
                    <Alert
                        title="Error Loading Cloud Accounts"
                        description={accountsError.message}
                        type="error"
                        showIcon
                    />
                ) : (
                    <Form form={form} layout="vertical">
                        {/* Target Cloud Selection Component */}
                        <TargetCloudSelector
                            processedCloudAccounts={processedCloudAccounts}
                            onTargetAccountChange={handleTargetAccountChange}
                            shouldPreselect={shouldPreselect}
                            isMigrateByVM={migrationTypeFlags.isMigrateByVM}
                            selectedAccount={selectedAccount}
                        />

                        {/* Credential Set Selection */}
                        {showCredentialSelector && (
                            <CredentialSetSelector
                                selectedSources={selectedSourcesWithCACreds}
                                selectedAccount={selectedAccount}
                                credentialSets={credentialSets}
                                onCredentialSetChange={handleCredentialSetChange}
                            />
                        )}

                        {/* Migration Type Selection Component - only show in migration mode */}
                        {!inspectionMode && (
                            <MigrationTypeSelector
                                isMigrateByVM={migrationTypeFlags.isMigrateByVM}
                                isMigrateNetAppFiles={migrationTypeFlags.isMigrateNetAppFiles}
                                migrationType={migrationType}
                                onMigrationTypeChange={handleMigrationTypeChange}
                                selectedAccount={selectedAccount}
                            />
                        )}

                        {/* Migration Options Component */}
                        <MigrationOptionsCheckboxes
                            showUseStoredInfoCheckbox={showUseStoredInfoCheckbox}
                            inspectionMode={showIgnoreValidationErrors ? false : inspectionMode}
                        />
                    </Form>
                )}
            </div>
        </Modal>
    );
};
